import { z } from "zod";

import type {
  PlaybackAlert,
  PlaybackPoint,
  SensorReading,
  TrackedVehicle,
  TrackingError,
  TrackingListRemovalReason,
  TrackingListState,
  TrackingSnapshot,
  TrackingVehicleCounts,
  VehicleTelemetry,
  VehicleTelemetryEvent,
} from "../domain";

// The live tracking protocol v1 on the wire (/live-docs). Parsed strictly
// where the contract is firm and leniently where it says a field may be
// missing or empty, so one odd record never drops a whole batch.

export const PROTOCOL_VERSION = 1;

const namedRefSchema = z.object({
  id: z.string(),
  nameEn: z.string(),
  nameAr: z.string(),
});

const contactSchema = z.object({
  id: z.string(),
  name: z.string(),
  phone: z.string().nullish(),
  email: z.string().nullish(),
});

const statusSchema = z.enum(["moving", "idle", "stopped", "offline"]);

const telemetrySchema = z.object({
  sequence: z.number(),
  recordedAt: z.string(),
  gpsTime: z.string().nullish(),
  lastCommunicationAt: z.string().nullish(),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  heading: z.number().nullish(),
  speedKph: z.number(),
  status: statusSchema,
  ignitionOn: z.boolean().nullish(),
  odometerKm: z.number().nullish(),
  // Read entry by entry (sensorsFromDto): a reading that doesn't parse is
  // dropped, never the position with it.
  sensors: z.unknown(),
});

type TelemetryDto = z.infer<typeof telemetrySchema>;

const vehicleSchema = z.object({
  id: z.string(),
  name: z.string(),
  nameAr: z.string().nullish(),
  code: z.string().nullish(),
  plateNumber: z.string().nullish(),
  locationLabel: z.string().nullish(),
  assetType: namedRefSchema.nullish(),
  account: namedRefSchema.nullish(),
  branch: namedRefSchema.nullish(),
  imei: z.string().nullish(),
  hasDevice: z.boolean().nullish(),
  hasCamera: z.boolean().nullish(),
  cameraDeviceId: z.string().nullish(),
  cameraVideoTransport: z.string().nullish(),
  driver: contactSchema.nullish(),
  supervisor: contactSchema.nullish(),
  // A record whose telemetry doesn't parse is kept, without a position.
  telemetry: z.unknown(),
});

type VehicleDto = z.infer<typeof vehicleSchema>;

const envelopeSchema = z.object({
  protocolVersion: z.literal(PROTOCOL_VERSION),
  streamId: z.string(),
});

const snapshotSchema = envelopeSchema.extend({
  capturedAt: z.string(),
  vehicles: z.array(z.unknown()),
});

const vehiclesSchema = envelopeSchema.extend({ vehicles: z.array(z.unknown()) });

const telemetryBatchSchema = envelopeSchema.extend({
  events: z.array(z.unknown()),
});

const telemetryEventSchema = z.object({
  vehicleId: z.string(),
  telemetry: telemetrySchema,
});

const removedSchema = envelopeSchema.extend({ vehicleIds: z.array(z.string()) });

const errorSchema = z.object({ code: z.string(), message: z.string() });

/** The status counters, on `GET /tracking/vehicles/counts` and the list feed. */
export const countsDtoSchema = z.object({
  total: z.number(),
  online: z.number(),
  moving: z.number(),
  idle: z.number(),
  stopped: z.number(),
  offline: z.number(),
  no_device: z.number(),
});

export function countsFromDto(dto: z.infer<typeof countsDtoSchema>): TrackingVehicleCounts {
  const { no_device: noDevice, ...counts } = dto;
  return { ...counts, noDevice };
}

const listStateSchema = telemetryBatchSchema.extend({ counts: countsDtoSchema });

const listRemovedSchema = removedSchema.extend({ reason: z.enum(["status", "scope"]) });

const listCountsSchema = envelopeSchema.extend({ counts: countsDtoSchema });

const sensorReadingSchema = z.object({
  sensorId: z.string(),
  sensorType: z.string(),
  probe: z.string(),
  label: z.string().nullish(),
  // A quantity we can't show yet is left out.
  quantity: z.enum(["TEMPERATURE", "HUMIDITY", "WEIGHT", "STATE"]),
  // Null for an assigned sensor that hasn't reported yet: still shown.
  value: z.number().nullish(),
  unit: z.string().nullish(),
  stateLabel: z.object({ en: z.string().nullish(), ar: z.string().nullish() }).nullish(),
});

const NO_SENSORS: readonly SensorReading[] = [];

/** `sensors`: one reading per probe. Anything but a list reads as none. */
function sensorsFromDto(sensors: unknown): readonly SensorReading[] {
  if (!Array.isArray(sensors) || sensors.length === 0) return NO_SENSORS;
  return sensors.flatMap((entry): SensorReading[] => {
    const reading = sensorReadingSchema.safeParse(entry);
    if (!reading.success) return [];
    const { label, unit, stateLabel, value, ...rest } = reading.data;
    return [
      {
        ...rest,
        value: value ?? null,
        label: label ?? null,
        unit: unit ?? null,
        stateLabel:
          stateLabel === null || stateLabel === undefined
            ? null
            : { en: stateLabel.en ?? null, ar: stateLabel.ar ?? null },
      },
    ];
  });
}

function telemetryFromDto(dto: TelemetryDto): VehicleTelemetry {
  return {
    sequence: dto.sequence,
    recordedAt: dto.recordedAt,
    gpsTime: dto.gpsTime ?? dto.recordedAt,
    lastCommunicationAt: dto.lastCommunicationAt ?? null,
    latitude: dto.latitude,
    longitude: dto.longitude,
    heading: dto.heading ?? null,
    speedKph: dto.speedKph,
    status: dto.status,
    ignitionOn: dto.ignitionOn ?? null,
    odometerKm: dto.odometerKm ?? null,
    sensors: sensorsFromDto(dto.sensors),
  };
}

/**
 * A vehicle's live telemetry (the snapshot, the lists and the feed). While
 * it's offline, the sensor values it carries are its last ones, not what the
 * sensors read now: each reads as no reading until the vehicle is back
 * online and reports again. Playback points keep their recorded values.
 */
function liveTelemetryFromDto(dto: TelemetryDto): VehicleTelemetry {
  const telemetry = telemetryFromDto(dto);
  if (telemetry.status !== "offline") return telemetry;
  return {
    ...telemetry,
    sensors: telemetry.sensors.map((reading) => ({ ...reading, value: null, stateLabel: null })),
  };
}

/** A record the feed sent that doesn't match the contract is dropped, not
 *  fatal -- but in development it's reported, so "the device hasn't sent a
 *  position" and "the client couldn't read it" don't look the same. */
/** The tracking debug log is on (TRACKING_DEBUG_KEY, application/telemetryDebug):
 *  dropped records are told on a deployed build too. */
function trackingDebugOn(): boolean {
  try {
    return localStorage.getItem("nasam:tracking-debug") !== null;
  } catch {
    return false;
  }
}

function reportRejected(what: string, id: unknown, error: z.ZodError): void {
  if (!import.meta.env.DEV && !trackingDebugOn()) return;
  console.warn(`[tracking] ${what} ${String(id)} doesn't match protocol v1 and was dropped:`, z.prettifyError(error));
}

function vehicleFromDto(dto: VehicleDto): TrackedVehicle {
  const telemetry =
    dto.telemetry === null || dto.telemetry === undefined
      ? null
      : telemetrySchema.safeParse(dto.telemetry);
  if (telemetry?.success === false) reportRejected("Telemetry of vehicle", dto.id, telemetry.error);
  return {
    id: dto.id,
    name: dto.name,
    nameAr: dto.nameAr ?? "",
    code: dto.code ?? "",
    plateNumber: dto.plateNumber ?? "",
    locationLabel: dto.locationLabel ?? "",
    assetType: dto.assetType ?? null,
    account: dto.account ?? null,
    branch: dto.branch ?? null,
    imei: dto.imei ?? null,
    hasDevice: dto.hasDevice ?? (dto.imei !== null && dto.imei !== undefined),
    // Left out (an older server): no camera, rather than one that can't start.
    hasCamera: dto.hasCamera ?? false,
    cameraDeviceId: dto.cameraDeviceId ?? null,
    cameraVideoTransport: dto.cameraVideoTransport ?? null,
    driver:
      dto.driver === null || dto.driver === undefined
        ? null
        : { ...dto.driver, phone: dto.driver.phone ?? null, email: dto.driver.email ?? null },
    supervisor:
      dto.supervisor === null || dto.supervisor === undefined
        ? null
        : {
            ...dto.supervisor,
            phone: dto.supervisor.phone ?? null,
            email: dto.supervisor.email ?? null,
          },
    telemetry: telemetry?.success ? liveTelemetryFromDto(telemetry.data) : null,
  };
}

/** Vehicles that parse; a malformed record is skipped, not fatal. The REST
 *  Asset List sends the same records as the socket. */
export function vehiclesFromDto(values: readonly unknown[]): TrackedVehicle[] {
  return values.flatMap((value) => {
    const parsed = vehicleSchema.safeParse(value);
    if (!parsed.success) {
      reportRejected("Vehicle", (value as { id?: unknown } | null)?.id, parsed.error);
    }
    return parsed.success ? [vehicleFromDto(parsed.data)] : [];
  });
}

export interface WireSnapshot {
  readonly streamId: string;
  readonly snapshot: TrackingSnapshot;
}

export interface WireVehicles {
  readonly streamId: string;
  readonly vehicles: readonly TrackedVehicle[];
}

export interface WireTelemetry {
  readonly streamId: string;
  readonly events: readonly VehicleTelemetryEvent[];
}

export interface WireRemoved {
  readonly streamId: string;
  readonly vehicleIds: readonly string[];
}

/** `null` when it isn't a v1 snapshot. */
export function parseSnapshot(value: unknown): WireSnapshot | null {
  const parsed = snapshotSchema.safeParse(value);
  if (!parsed.success) return null;
  return {
    streamId: parsed.data.streamId,
    snapshot: {
      capturedAt: parsed.data.capturedAt,
      vehicles: vehiclesFromDto(parsed.data.vehicles),
    },
  };
}

export function parseVehicles(value: unknown): WireVehicles | null {
  const parsed = vehiclesSchema.safeParse(value);
  if (!parsed.success) return null;
  return { streamId: parsed.data.streamId, vehicles: vehiclesFromDto(parsed.data.vehicles) };
}

function telemetryEventsFromDto(values: readonly unknown[]): VehicleTelemetryEvent[] {
  return values.flatMap((event) => {
    const item = telemetryEventSchema.safeParse(event);
    if (!item.success) {
      reportRejected("Telemetry event for", (event as { vehicleId?: unknown } | null)?.vehicleId, item.error);
    }
    return item.success
      ? [{ vehicleId: item.data.vehicleId, telemetry: liveTelemetryFromDto(item.data.telemetry) }]
      : [];
  });
}

/** `tracking:telemetry` and `tracking:list:telemetry`. */
export function parseTelemetry(value: unknown): WireTelemetry | null {
  const parsed = telemetryBatchSchema.safeParse(value);
  if (!parsed.success) return null;
  return { streamId: parsed.data.streamId, events: telemetryEventsFromDto(parsed.data.events) };
}

export function parseRemoved(value: unknown): WireRemoved | null {
  const parsed = removedSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export interface WireListState {
  readonly streamId: string;
  readonly state: TrackingListState;
}

/** `tracking:list:watch`'s acknowledgement. */
export function parseListState(value: unknown): WireListState | null {
  const parsed = listStateSchema.safeParse(value);
  if (!parsed.success) return null;
  return {
    streamId: parsed.data.streamId,
    state: {
      events: telemetryEventsFromDto(parsed.data.events),
      counts: countsFromDto(parsed.data.counts),
    },
  };
}

export interface WireListRemoved extends WireRemoved {
  readonly reason: TrackingListRemovalReason;
}

export function parseListRemoved(value: unknown): WireListRemoved | null {
  const parsed = listRemovedSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export interface WireListCounts {
  readonly streamId: string;
  readonly counts: TrackingVehicleCounts;
}

export function parseListCounts(value: unknown): WireListCounts | null {
  const parsed = listCountsSchema.safeParse(value);
  return parsed.success
    ? { streamId: parsed.data.streamId, counts: countsFromDto(parsed.data.counts) }
    : null;
}

/** A request's acknowledgement error (`{ error }`) or a `tracking:error`. */
export function parseError(value: unknown): TrackingError | null {
  const direct = errorSchema.safeParse(value);
  if (direct.success) return direct.data;
  const wrapped = z.object({ error: errorSchema }).safeParse(value);
  return wrapped.success ? wrapped.data.error : null;
}

const playbackResponseSchema = z.object({ data: z.array(z.unknown()) });
// Built once: a playback can hold up to 50,000 points.
const playbackPointSchema = telemetrySchema.extend({
  locationLabel: z.string().nullish(),
  battery: z.number().nullish(),
  // Read alert by alert (playbackAlertsFromDto): one that doesn't parse is
  // dropped, never the point with it.
  // Optional: a point (or an older backend) may carry none.
  alerts: z.unknown().optional(),
});
const playbackAlertSchema = z.object({
  id: z.string().min(1),
  occurredAt: z.string(),
  alertType: z.string(),
  alertNameEn: z.string().nullish(),
  alertNameAr: z.string().nullish(),
  triggeredValueEn: z.string().nullish(),
  triggeredValueAr: z.string().nullish(),
  location: z
    .object({
      latitude: z.number().min(-90).max(90),
      longitude: z.number().min(-180).max(180),
    })
    .nullish(),
});

/** Most points fired nothing: they share one empty list. */
const NO_ALERTS: readonly PlaybackAlert[] = Object.freeze([]);

function playbackAlertsFromDto(
  value: unknown,
  point: { readonly latitude: number; readonly longitude: number },
): readonly PlaybackAlert[] {
  if (!Array.isArray(value) || value.length === 0) return NO_ALERTS;
  return value.flatMap((item) => {
    const alert = playbackAlertSchema.safeParse(item);
    if (!alert.success) return [];
    const occurredMs = Date.parse(alert.data.occurredAt);
    if (!Number.isFinite(occurredMs)) return [];
    const { location } = alert.data;
    return [
      {
        id: alert.data.id,
        occurredAt: alert.data.occurredAt,
        occurredMs,
        alertType: alert.data.alertType,
        alertNameEn: alert.data.alertNameEn ?? "",
        alertNameAr: alert.data.alertNameAr ?? "",
        triggeredValueEn: alert.data.triggeredValueEn ?? "",
        triggeredValueAr: alert.data.triggeredValueAr ?? "",
        position: location ?? { latitude: point.latitude, longitude: point.longitude },
        hasOwnLocation: location !== null && location !== undefined,
      },
    ];
  });
}

/** `GET /tracking/vehicles/:assetId/playback`: points in the telemetry shape
 *  plus their location label, battery and the alerts fired from each, oldest
 *  first. A point that doesn't parse is skipped. */
export function parsePlaybackPoints(value: unknown): PlaybackPoint[] {
  const parsed = playbackResponseSchema.parse(value);
  return parsed.data.flatMap((item) => {
    const point = playbackPointSchema.safeParse(item);
    return point.success
      ? [
          {
            ...telemetryFromDto(point.data),
            locationLabel: point.data.locationLabel ?? "",
            battery: point.data.battery ?? null,
            alerts: playbackAlertsFromDto(point.data.alerts, point.data),
          },
        ]
      : [];
  });
}
